import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-auction-email`;

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase?.auth?.getSession();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`
  };
};

const sendEmail = async (type, to, data) => {
  if (!to) return { success: false, error: 'No recipient email' };
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, to, data })
    });
    const result = await response?.json();
    if (!response?.ok) throw new Error(result?.error || 'Email send failed');
    return { success: true, id: result?.id };
  } catch (error) {
    console.error(`[auctionEmailService] Failed to send ${type} email:`, error?.message);
    return { success: false, error: error?.message };
  }
};

/**
 * Get user email from Supabase by user ID
 */
const getUserEmail = async (userId) => {
  if (!userId) return null;
  try {
    const { data } = await supabase
      ?.from('user_profiles')
      ?.select('email, full_name')
      ?.eq('id', userId)
      ?.single();
    return data;
  } catch {
    return null;
  }
};

const auctionEmailService = {
  /**
   * Send bid placed confirmation to the bidder
   */
  async sendBidPlacedEmail(bidderId, auctionData) {
    const user = await getUserEmail(bidderId);
    if (!user?.email) return;
    return sendEmail('bid_placed', user?.email, {
      auctionTitle: auctionData?.title || auctionData?.collectible_name || 'Auction Item',
      bidAmount: auctionData?.bidAmount,
      endDate: auctionData?.endDate
        ? new Date(auctionData.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : 'TBD'
    });
  },

  /**
   * Send outbid notification to the previous highest bidder
   */
  async sendOutbidEmail(previousBidderId, auctionData) {
    const user = await getUserEmail(previousBidderId);
    if (!user?.email) return;
    return sendEmail('outbid', user?.email, {
      auctionTitle: auctionData?.title || auctionData?.collectible_name || 'Auction Item',
      yourBid: auctionData?.yourBid,
      currentBid: auctionData?.currentBid
    });
  },

  /**
   * Send auction won notification to the winning bidder
   */
  async sendAuctionWonEmail(winnerId, auctionData) {
    const user = await getUserEmail(winnerId);
    if (!user?.email) return;
    return sendEmail('auction_won', user?.email, {
      auctionTitle: auctionData?.title || auctionData?.collectible_name || 'Auction Item',
      winningBid: auctionData?.winningBid || auctionData?.current_bid
    });
  },

  /**
   * Send auction sold notification to the seller
   */
  async sendAuctionSoldEmail(sellerId, auctionData) {
    const user = await getUserEmail(sellerId);
    if (!user?.email) return;
    return sendEmail('auction_sold', user?.email, {
      auctionTitle: auctionData?.title || auctionData?.collectible_name || 'Auction Item',
      finalPrice: auctionData?.finalPrice || auctionData?.current_bid,
      totalBids: auctionData?.totalBids
    });
  },

  /**
   * Send dispute opened notification to both buyer and seller
   */
  async sendDisputeOpenedEmail(buyerId, sellerId, transactionData) {
    const [buyer, seller] = await Promise.all([
      getUserEmail(buyerId),
      getUserEmail(sellerId)
    ]);
    const emailData = {
      transactionId: transactionData?.id,
      itemTitle: transactionData?.itemTitle || 'Item',
      amount: transactionData?.amount
    };
    const promises = [];
    if (buyer?.email) promises?.push(sendEmail('dispute_opened', buyer?.email, emailData));
    if (seller?.email) promises?.push(sendEmail('dispute_opened', seller?.email, emailData));
    return Promise.all(promises);
  },

  /**
   * Send dispute resolved notification to both buyer and seller
   */
  async sendDisputeResolvedEmail(buyerId, sellerId, transactionData, resolution) {
    const [buyer, seller] = await Promise.all([
      getUserEmail(buyerId),
      getUserEmail(sellerId)
    ]);
    const emailData = {
      transactionId: transactionData?.id,
      itemTitle: transactionData?.itemTitle || 'Item',
      resolution
    };
    const promises = [];
    if (buyer?.email) promises?.push(sendEmail('dispute_resolved', buyer?.email, emailData));
    if (seller?.email) promises?.push(sendEmail('dispute_resolved', seller?.email, emailData));
    return Promise.all(promises);
  },

  /**
   * Send new review notification to the reviewed user
   */
  async sendNewReviewEmail(reviewedUserId, reviewData) {
    const user = await getUserEmail(reviewedUserId);
    if (!user?.email) return;
    return sendEmail('new_review', user?.email, {
      reviewerName: reviewData?.reviewerName,
      rating: reviewData?.rating,
      reviewText: reviewData?.reviewText,
      role: reviewData?.role
    });
  }
};

export default auctionEmailService;
